import * as React from "react";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";

import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { cn } from "../../lib/utils.js";
import {
  GOAL_STATUS_LABELS,
  ASSIGNMENT_STATUS_LABELS,
  GOAL_SCOPE_LABELS,
  type GoalScope,
  type GoalStatus,
  type AssignmentStatus,
  PROBATION_OUTCOME_DONE_LABELS,
  type ProbationOutcome,
} from "./types.js";

// Small pieces shared by the list page, the detail sections and the admin
// panels. Everything that would otherwise be copy-pasted three times lives here.

// Employees as searchable options: name as the label, code underneath, and
// both matched by the Combobox search.
export function toEmployeeOptions(
  employees: { id: string; firstName: string; lastName: string; employeeCode: string }[],
): { value: string; label: string; sub: string }[] {
  return employees.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}`, sub: e.employeeCode }));
}

// Every date in the performance module reads in US format, MM-DD-YYYY.
export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${m}-${d}-${y}` : value;
}

// `collapsible` turns the title into a toggle that opens and closes the card's
// body; header actions stay visible either way. Off by default, so existing
// cards are unchanged. A collapsible card starts closed unless `defaultOpen`.
export function SectionCard({
  title,
  leading,
  description,
  actions,
  children,
  className,
  collapsible = false,
  defaultOpen = false,
}: {
  // Omit `title` when the page header already names the card; `leading` (e.g.
  // a search box) then takes the left side of the card header instead.
  title?: string;
  leading?: React.ReactNode;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const bodyId = React.useId();
  const expanded = !collapsible || open;

  const heading = title ? (
    <div>
      <h2 className="text-[15px] font-bold">{title}</h2>
      {description && <p className="mt-0.5 text-[13px] text-surface-500">{description}</p>}
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2">{leading}</div>
  );

  return (
    <section className={cn("rounded-lg border border-surface-200 bg-surface-0", className)}>
      <header
        className={cn(
          "flex flex-wrap items-start justify-between gap-3 px-5 py-3.5",
          expanded && "border-b border-surface-100",
        )}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={bodyId}
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
          >
            <ChevronDown
              size={18}
              className={cn("mt-0.5 shrink-0 text-surface-400 transition-transform", !open && "-rotate-90")}
            />
            {heading}
          </button>
        ) : (
          heading
        )}
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {expanded && (
        <div id={bodyId} className="p-5">
          {children}
        </div>
      )}
    </section>
  );
}

// Rows per page for performance lists, matching the Expenses list.
export const PAGE_SIZE = 20;

// Client-side pages over a fully loaded list — shared app-wide in lib/usePaged.
export { usePaged } from "../../lib/usePaged.js";

export function EmptyState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <p className="text-[13px] text-surface-400">{message}</p>
      {action}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string | undefined;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-surface-400">{hint}</p>}
    </div>
  );
}

// A bordered block inside a SectionCard with an uppercase heading and optional
// right-aligned badges/actions (e.g. "Employee self-review", "HR decision").
// `collapsible` adds the same chevron open/close toggle SectionCard has; the
// header (title + actions, e.g. a status badge) stays visible when closed.
export function SubPanel({
  title,
  actions,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const bodyId = React.useId();
  const expanded = !collapsible || open;
  const heading = <h4 className="text-[13px] font-bold tracking-wide text-surface-600 uppercase">{title}</h4>;

  return (
    <div className="rounded-md border border-surface-200 p-4">
      <div className={cn("flex flex-wrap items-center justify-between gap-2", expanded && "mb-3")}>
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={bodyId}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <ChevronDown
              size={16}
              className={cn("shrink-0 text-surface-400 transition-transform", !open && "-rotate-90")}
            />
            {heading}
          </button>
        ) : (
          heading
        )}
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {expanded && <div id={bodyId}>{children}</div>}
    </div>
  );
}

export function LockedNotice({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md bg-surface-50 px-3 py-2 text-[12px] text-surface-500">{children}</p>
  );
}

// Row edit / delete, drawn exactly as the Expenses list draws them: ghost icon
// Buttons at 14px, edit in grey and delete in red. Shared so every performance
// table uses one definition.
export function EditIconButton({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <Button onClick={onClick} title="Edit" aria-label={`Edit ${name}`} variant="ghost" size="icon" className="text-surface-500">
      <Pencil size={14} />
    </Button>
  );
}

// `blockedReason` set = the row is still in use: Delete is disabled and the
// reason becomes its tooltip. A disabled button fires no pointer events, so the
// tooltip lives on a wrapper span that still receives the hover.
export function DeleteIconButton({
  name,
  onClick,
  blockedReason,
}: {
  name: string;
  onClick: () => void;
  blockedReason?: string | undefined;
}) {
  return (
    <span title={blockedReason ?? "Delete"} className={blockedReason ? "cursor-not-allowed" : undefined}>
      <Button
        onClick={onClick}
        disabled={!!blockedReason}
        aria-label={blockedReason ? `Cannot delete ${name}: ${blockedReason}` : `Delete ${name}`}
        variant="ghost"
        size="icon"
        className="text-error"
      >
        <Trash2 size={14} />
      </Button>
    </span>
  );
}

export function GoalStatusBadge({ status }: { status: GoalStatus | null }) {
  if (!status) return <span className="text-surface-400">—</span>;
  const variant = status === "achieved" ? "success" : status === "partially_achieved" ? "warning" : "error";
  return <Badge tone={variant}>{GOAL_STATUS_LABELS[status]}</Badge>;
}

export function AssignmentStatusBadge({ status }: { status: AssignmentStatus | null }) {
  if (!status) return <span className="text-surface-400">Not set</span>;
  const variant =
    status === "above_expectation" ? "success" : status === "meet_expectation" ? "info" : "error";
  return <Badge tone={variant}>{ASSIGNMENT_STATUS_LABELS[status]}</Badge>;
}

export function GoalScopeBadge({ scope }: { scope: string }) {
  const label = GOAL_SCOPE_LABELS[scope as GoalScope] ?? scope;
  const tone = scope === "TEAM" ? "info" : scope === "INDIVIDUAL" ? "accent" : "outline";
  return <Badge tone={tone}>{label}</Badge>;
}

export function OutcomeBadge({ outcome }: { outcome: ProbationOutcome | null }) {
  if (!outcome) return <Badge tone="neutral">Open</Badge>;
  const variant =
    outcome === "CONFIRM" ? "success" : outcome === "EXTEND" ? "warning" : outcome === "PIP" ? "info" : "error";
  return <Badge tone={variant}>{PROBATION_OUTCOME_DONE_LABELS[outcome]}</Badge>;
}

// Every review is an annual one.
export function ReviewTypeBadge() {
  return <Badge tone="accent">Annual</Badge>;
}

// Probation is a status on the annual review.
export function ProbationBadge({ extended = false }: { extended?: boolean }) {
  return <Badge tone="warning">{extended ? "On probation (extended)" : "On probation"}</Badge>;
}

// The ONE type label a review shows: "Temporary" for a review the viewer covers
// as temporary manager, else "On probation" while the employee is on
// probation, else "Annual" (which is what it becomes once probation ends).
export function ReviewKindBadge({
  temporary = false,
  onProbation,
  extended = false,
}: {
  temporary?: boolean;
  onProbation: boolean;
  extended?: boolean;
}) {
  if (temporary) return <Badge tone="info">Temporary</Badge>;
  if (onProbation) return <ProbationBadge extended={extended} />;
  return <ReviewTypeBadge />;
}

// A 0-5 rating with at most one decimal place. The step and bounds mirror
// isValidRating on the server; the server still re-validates on save.
export function RatingInput({
  id,
  value,
  onChange,
  disabled,
}: {
  id?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <Input
      id={id}
      type="number"
      min={0}
      max={5}
      step={0.1}
      inputMode="decimal"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === "" ? null : Number(raw));
      }}
      placeholder="0.0"
    />
  );
}

// The computed rating, shown live so the user can see what their inputs produce
// before saving. Duplicates the server formula deliberately — the server value
// is authoritative, this is only a preview.
export function previewPeriodRating(
  utilization: number | null,
  productivity: number | null,
  qcPercent: number | null,
  qcApplies: boolean,
): number | null {
  if (utilization == null || productivity == null) return null;
  if (!qcApplies) return Math.round(((utilization + productivity) / 2 + Number.EPSILON) * 10) / 10;
  if (qcPercent == null) return null;
  const qcOn5 = (qcPercent / 100) * 5;
  return Math.round(((utilization + productivity + qcOn5) / 3 + Number.EPSILON) * 10) / 10;
}

export function previewFinalRating(performance: (number | null)[], personal: (number | null)[]): number | null {
  if (performance.some((v) => v == null) || personal.some((v) => v == null)) return null;
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  return Math.round((avg(performance as number[]) * 0.7 + avg(personal as number[]) * 0.3 + Number.EPSILON) * 10) / 10;
}

export function RatingPill({ value, label }: { value: number | null; label: string }) {
  return (
    <div className="flex items-baseline gap-2 rounded-md bg-surface-50 px-3 py-2">
      <span className="text-[11px] font-medium tracking-wide text-surface-500 uppercase">{label}</span>
      <span className="text-lg font-bold tabular-nums">{value == null ? "—" : value.toFixed(1)}</span>
      <span className="text-[11px] text-surface-400">/ 5</span>
    </div>
  );
}
